import { MelodyLib, midiToNoteLabel, type CaptureDiagnosis } from '@tunasong/music-lib'
import type { Melody, MelodySource } from '@tunasong/schemas'
import { Button } from '@tunasong/ui-lib'
import { useState } from 'react'
import type { FC } from 'react'
import { MelodyRoll } from '../melody-roll/melody-roll.js'
import { applyCapture } from './apply-capture.js'
import styles from './capture-review.module.css'

/**
 * What the take produced, before it is written anywhere.
 *
 * The review holds the RAW notes, not the quantized ones, and re-quantizes on every change.
 * Restoring a dropped note is then a shift of one raw note followed by the same quantization
 * everything else went through - so a restored note that lands on a slot a louder note already owns
 * is dropped again, visibly, instead of quietly overwriting it.
 *
 * An empty take is a first-class outcome and not an error: the three-way capture diagnosis and its
 * measured numbers are shown, as the Pitch Trainer shows them, because a failure on a real voice
 * should produce data rather than a shrug.
 *
 * The roll here is `MelodyRoll` and not the full `MelodyEditor`, for two reasons. The review is a
 * decision - keep this take or play it again - and an edit made here would be thrown away by the
 * next take, so offering one would be a lie. And the editor now hosts the capture dialog itself, so
 * rendering it from inside the dialog would close an import cycle through this file.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §5
 */

const REASON_TEXT: Record<MelodyLib.DropReason, string> = {
  'too-short': 'shorter than a 64th note',
  'slot-taken': 'a louder note landed on the same step',
  'before-take': 'played before the first beat',
}

const DIAGNOSIS_TEXT: Record<CaptureDiagnosis['kind'], string> = {
  'no-signal': 'Nothing reached the microphone. Check that the right input device is selected.',
  'too-quiet': 'Sound arrived, but never got loud enough to measure. Move closer, or raise the input gain.',
  unclear: 'Loud enough, but no steady pitch could be found. Try a quieter room, or a more open tone.',
  ok: 'The take was heard, but nothing in it was long enough to be a note.',
}

export interface CaptureReviewProps {
  melody: Melody
  raw: MelodyLib.RawNote[]
  source: MelodySource
  /** Set for an audio take, so an empty one can say which of the three things went wrong. */
  diagnosis?: CaptureDiagnosis | null
  onAccept: (melody: Melody) => void
  onRetake: () => void
  onCancel: () => void
  className?: string
}

export const CaptureReview: FC<CaptureReviewProps> = props => {
  const { melody, raw: captured, source, diagnosis = null, onAccept, onRetake, onCancel, className } = props
  const [raw, setRaw] = useState(captured)

  const { notes, dropped } = MelodyLib.quantizeRawNotes(raw, {
    bpm: melody.tempo.bpm,
    meter: melody.tempo.meter,
    grid: melody.grid,
  })
  const reviewed = { ...melody, notes }

  const handleRestore = (sourceIndex: number) =>
    setRaw(current =>
      MelodyLib.shiftRawNote(current, sourceIndex, MelodyLib.gridSlotMs(melody.grid, melody.tempo.bpm))
    )

  return (
    <div className={className ? `${styles.root} ${className}` : styles.root}>
      {notes.length > 0 ? <MelodyRoll melody={reviewed} /> : null}

      {dropped.length > 0 ? (
        <>
          <span className={styles.droppedHeading}>
            {`${dropped.length} note${dropped.length === 1 ? '' : 's'} did not make it onto the grid.`}
          </span>
          {/* Named, not `aria-labelledby` the sentence above: the count changes as notes are restored. */}
          <ul className={styles.dropped} aria-label="Dropped notes">
            {dropped.map(note => (
              <li className={styles.droppedItem} key={`${note.sourceIndex}-${note.reason}`}>
                <span>{`${midiToNoteLabel(note.midi)} — ${REASON_TEXT[note.reason]}`}</span>
                {/* One slot later, then re-quantized: the restored note earns its place like the rest. */}
                <Button onClick={() => handleRestore(note.sourceIndex)}>Restore a step later</Button>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {notes.length === 0 && diagnosis ? (
        <div className={styles.diagnosis} role="status">
          <span>{DIAGNOSIS_TEXT[diagnosis.kind]}</span>
          {/* The numbers the verdict was read from, so it can be checked rather than believed. */}
          <span className={styles.measurements}>
            {`Peak ${diagnosis.peakDb.toFixed(1)} dBFS · floor ${diagnosis.noiseFloorDb.toFixed(1)} dBFS · ` +
              `best clarity ${diagnosis.bestClarity.toFixed(2)} · ` +
              `${Math.round(diagnosis.voicedFraction * 100)}% of frames counted`}
          </span>
        </div>
      ) : null}

      {notes.length === 0 && !diagnosis ? <span role="status">Nothing was played during the take.</span> : null}

      <div className={styles.actions}>
        {notes.length > 0 ? (
          <Button onClick={() => onAccept(applyCapture(melody, notes, source))}>Accept</Button>
        ) : null}
        <Button onClick={onRetake}>Record again</Button>
        <Button onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  )
}
